import { useState, useEffect, type FormEvent } from 'react';
import { useTours, type Tour } from '@/hooks/useTours';
import { useTourVariants, type TourVariant, type TourVariantInput } from '@/hooks/useTourVariants';
import { supabase } from '@/lib/supabase';

interface VariantForm {
  id: string | null;
  name: string;
  sku: string;
  option1_name: string;
  option1_value: string;
  option2_name: string;
  option2_value: string;
  option3_name: string;
  option3_value: string;
  price: string;
  compare_at_price: string;
  stock_quantity: string;
  image: string;
}

const emptyVariant: VariantForm = {
  id: null,
  name: '',
  sku: '',
  option1_name: '',
  option1_value: '',
  option2_name: '',
  option2_value: '',
  option3_name: '',
  option3_value: '',
  price: '',
  compare_at_price: '',
  stock_quantity: '10',
  image: '',
};

interface TourForm {
  id: string | null;
  title: string;
  slug: string;
  description: string;
  price: string;
  compare_at_price: string;
  image: string;
  category: string;
  duration: string;
  spots: string;
  group_type: string;
  status: 'active' | 'draft' | 'archived';
  is_service: boolean;
  track_inventory: boolean;
  stock_quantity: string;
  currency: string;
}

const emptyTour: TourForm = {
  id: null,
  title: '',
  slug: '',
  description: '',
  price: '',
  compare_at_price: '',
  image: '',
  category: 'Vietnam',
  duration: '',
  spots: '12',
  group_type: 'Small group',
  status: 'active',
  is_service: true,
  track_inventory: false,
  stock_quantity: '50',
  currency: 'USD',
};

export default function TourProductManager() {
  const { tours, loading: toursLoading, error: toursError, refetch: refetchTours } = useTours();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<TourForm>(emptyTour);
  const [variants, setVariants] = useState<VariantForm[]>([]);
  const [variantForm, setVariantForm] = useState<VariantForm>(emptyVariant);
  const [showVariantForm, setShowVariantForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const isEditing = !!form.id;
  const selectedTourId = form.id;
  const { variants: dbVariants, createVariant, updateVariant, deleteVariant } = useTourVariants(selectedTourId || undefined);

  const openCreate = () => {
    setForm(emptyTour);
    setVariants([]);
    setVariantForm(emptyVariant);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = async (tour: Tour) => {
    setForm({
      id: tour.id,
      title: tour.title,
      slug: tour.slug,
      description: tour.description || '',
      price: String(tour.price),
      compare_at_price: '',
      image: tour.image,
      category: tour.category,
      duration: tour.duration,
      spots: String(tour.spots),
      group_type: tour.group_type,
      status: 'active',
      is_service: true,
      track_inventory: false,
      stock_quantity: '50',
      currency: 'USD',
    });
    setVariants([]);
    setVariantForm(emptyVariant);
    setFormError(null);
    setShowForm(true);
  };

  // Load db variants when editing
  useEffect(() => {
    if (dbVariants && dbVariants.length > 0) {
      setVariants(
        dbVariants.map((v) => ({
          id: v.id,
          name: v.name || '',
          sku: v.sku || '',
          option1_name: v.option1_name || '',
          option1_value: v.option1_value || '',
          option2_name: v.option2_name || '',
          option2_value: v.option2_value || '',
          option3_name: v.option3_name || '',
          option3_value: v.option3_value || '',
          price: v.price ? String(v.price) : '',
          compare_at_price: v.compare_at_price ? String(v.compare_at_price) : '',
          stock_quantity: String(v.stock_quantity || 0),
          image: v.image || '',
        }))
      );
    }
  }, [dbVariants]);

  const syncToLandingSt = async (tourId: string) => {
    setSyncing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/vietnamtravel-sync-product-to-landingst`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token || ''}`,
          },
          body: JSON.stringify({ tour_id: tourId }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        console.warn('Landing.st sync warning:', data.error || 'Unknown error');
        return { success: false, error: data.error || 'Sync failed' };
      }
      return { success: true, error: null };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Sync error' };
    } finally {
      setSyncing(false);
    }
  };

  const handleManualSync = async (tourId: string) => {
    setSyncingId(tourId);
    const result = await syncToLandingSt(tourId);
    if (result.success) {
      setToast('Synced to landing.st successfully.');
    } else {
      setToast(`Sync failed: ${result.error}`);
    }
    window.setTimeout(() => setToast(''), 4000);
    setSyncingId(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    const price = Number(form.price);
    if (!form.title.trim()) {
      setFormError('Tour name is required.');
      return;
    }
    if (!form.slug.trim()) {
      setFormError('Slug is required.');
      return;
    }
    if (Number.isNaN(price) || price <= 0) {
      setFormError('Please enter a valid price greater than 0.');
      return;
    }

    setSaving(true);
    try {
      const tourPayload = {
        title: form.title.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || null,
        price,
        image: form.image.trim() || 'https://readdy.ai/api/search-image?query=Beautiful%20Vietnam%20travel%20landscape%20tour%20destination%20scenic%20view&width=800&height=600&seq=tour-default&orientation=landscape',
        category: form.category.trim(),
        duration: form.duration.trim(),
        spots: Number(form.spots) || 12,
        group_type: form.group_type.trim(),
      };

      let tourId = form.id;
      if (isEditing && tourId) {
        const { error: updateErr } = await supabase
          .from('vietnamtravel_tours')
          .update(tourPayload)
          .eq('id', tourId);
        if (updateErr) throw new Error(updateErr.message);
      } else {
        const { data: newTour, error: insertErr } = await supabase
          .from('vietnamtravel_tours')
          .insert(tourPayload)
          .select()
          .single();
        if (insertErr) throw new Error(insertErr.message);
        tourId = newTour.id;
      }

      // Save variants
      if (tourId) {
        for (const v of variants) {
          const variantPayload: TourVariantInput = {
            tour_id: tourId,
            name: v.name || null,
            sku: v.sku || null,
            option1_name: v.option1_name || null,
            option1_value: v.option1_value || null,
            option2_name: v.option2_name || null,
            option2_value: v.option2_value || null,
            option3_name: v.option3_name || null,
            option3_value: v.option3_value || null,
            price: v.price ? Number(v.price) : null,
            compare_at_price: v.compare_at_price ? Number(v.compare_at_price) : null,
            stock_quantity: Number(v.stock_quantity) || 0,
            image: v.image || null,
          };
          if (v.id) {
            await updateVariant(v.id, variantPayload);
          } else {
            await createVariant(variantPayload);
          }
        }
      }

      // Auto-sync to landing.st
      if (tourId) {
        const syncResult = await syncToLandingSt(tourId);
        if (!syncResult.success) {
          setToast(`${isEditing ? 'Tour updated' : 'Tour created'}. Landing.st sync: ${syncResult.error}`);
        } else {
          setToast(`${isEditing ? 'Tour updated' : 'Tour created'} and synced to landing.st.`);
        }
      }

      setShowForm(false);
      window.setTimeout(() => setToast(''), 4000);
      refetchTours();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save tour');
    } finally {
      setSaving(false);
    }
  };

  const addVariant = () => {
    setVariantForm(emptyVariant);
    setShowVariantForm(true);
  };

  const saveVariant = () => {
    if (!variantForm.name.trim() && !variantForm.sku.trim()) {
      setFormError('Variant must have a name or SKU.');
      return;
    }
    if (variantForm.id) {
      setVariants((prev) => prev.map((v) => (v.id === variantForm.id ? { ...variantForm } : v)));
    } else {
      setVariants((prev) => [...prev, { ...variantForm, id: `temp-${Date.now()}` }]);
    }
    setShowVariantForm(false);
    setVariantForm(emptyVariant);
    setFormError(null);
  };

  const removeVariant = (id: string | null) => {
    if (!id) return;
    setVariants((prev) => prev.filter((v) => v.id !== id));
  };

  const editVariant = (v: VariantForm) => {
    setVariantForm(v);
    setShowVariantForm(true);
  };

  if (toursLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3 text-secondary-500">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <span className="text-sm">Loading tours...</span>
        </div>
      </div>
    );
  }

  if (toursError) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">Something went wrong</h3>
        <p className="mt-1 text-sm text-secondary-500">{toursError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-secondary-900">Tour Products</h2>
          <p className="text-sm text-secondary-500 mt-0.5">
            Create and manage tours following the landing.st UFD v1.0 product standard. {tours.length} tour{tours.length !== 1 ? 's' : ''} total.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line text-base" />
          New tour product
        </button>
      </div>

      {toast && (
        <div className="flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
          <i className="ri-checkbox-circle-line text-base" />
          {toast}
        </div>
      )}

      {tours.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-secondary-200">
          <div className="w-16 h-16 mx-auto rounded-full bg-secondary-100 flex items-center justify-center">
            <i className="ri-suitcase-line text-2xl text-secondary-500" />
          </div>
          <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">No tours yet</h3>
          <p className="mt-1 text-sm text-secondary-500">Create a tour product to sync with landing.st.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tours.map((t) => (
            <div
              key={t.id}
              className="bg-white rounded-lg border border-secondary-200 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
            >
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary-100 shrink-0">
                <img src={t.image} alt={t.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-secondary-900 truncate">{t.title}</span>
                  <span className="text-xs font-semibold rounded-full px-2.5 py-0.5 whitespace-nowrap bg-primary-100 text-primary-700">
                    {t.category}
                  </span>
                  <span className="text-xs font-semibold rounded-full px-2.5 py-0.5 whitespace-nowrap bg-secondary-100 text-secondary-700">
                    {t.duration}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-secondary-400">
                  ${Number(t.price).toLocaleString()} · {t.spots} spots · {t.group_type} · Slug: {t.slug}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleManualSync(t.id)}
                  disabled={syncingId === t.id}
                  className="inline-flex items-center justify-center gap-1.5 text-xs font-medium text-primary-600 border border-primary-300 hover:border-primary-400 hover:bg-primary-50 disabled:opacity-60 rounded-md px-3 py-1.5 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {syncingId === t.id ? (
                    <i className="ri-loader-4-line animate-spin text-sm" />
                  ) : (
                    <i className="ri-refresh-line text-sm" />
                  )}
                  Sync
                </button>
                <button
                  onClick={() => openEdit(t)}
                  className="inline-flex items-center justify-center gap-1.5 text-xs font-medium text-secondary-600 border border-secondary-300 hover:border-secondary-400 rounded-md px-3 py-1.5 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-pencil-line" />
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => !saving && setShowForm(false)} />
          <div className="relative bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-bold text-secondary-900">
                {isEditing ? 'Edit tour product' : 'New tour product'}
              </h3>
              <button
                onClick={() => !saving && setShowForm(false)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-secondary-500 hover:bg-secondary-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                    Tour name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Ha Long Bay 2 Days"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                    Slug <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="e.g. ha-long-bay-2-days"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Short description of the tour..."
                  rows={3}
                  className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                    Price (USD) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="199"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Compare at price</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={form.compare_at_price}
                    onChange={(e) => setForm({ ...form, compare_at_price: e.target.value })}
                    placeholder="299"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Currency</label>
                  <select
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  >
                    <option value="USD">USD</option>
                    <option value="VND">VND</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Category</label>
                  <input
                    type="text"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="Vietnam"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Duration</label>
                  <input
                    type="text"
                    value={form.duration}
                    onChange={(e) => setForm({ ...form, duration: e.target.value })}
                    placeholder="2 days 1 night"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Spots</label>
                  <input
                    type="number"
                    min="1"
                    value={form.spots}
                    onChange={(e) => setForm({ ...form, spots: e.target.value })}
                    placeholder="12"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Image URL</label>
                <input
                  type="text"
                  value={form.image}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  placeholder="https://..."
                  className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Group type</label>
                <input
                  type="text"
                  value={form.group_type}
                  onChange={(e) => setForm({ ...form, group_type: e.target.value })}
                  placeholder="Small group"
                  className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                />
              </div>

              {/* Variants section */}
              <div className="border-t border-secondary-200 pt-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-secondary-800">Variants</h4>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-circle-line" />
                    Add variant
                  </button>
                </div>

                {variants.length === 0 ? (
                  <p className="text-xs text-secondary-400">No variants. Add variants if this tour has options (room type, vehicle, etc.).</p>
                ) : (
                  <div className="space-y-2">
                    {variants.map((v) => (
                      <div key={v.id} className="flex items-center gap-3 bg-secondary-50 rounded-md px-3 py-2">
                        <div className="flex-1 min-w-0 text-xs text-secondary-700 truncate">
                          <span className="font-semibold">{v.name || v.sku || 'Unnamed'}</span>
                          {v.option1_name && v.option1_value && (
                            <span className="ml-2 text-secondary-500">{v.option1_name}: {v.option1_value}</span>
                          )}
                          {v.price && <span className="ml-2 text-primary-600 font-medium">${Number(v.price).toLocaleString()}</span>}
                        </div>
                        <button
                          type="button"
                          onClick={() => editVariant(v)}
                          className="text-secondary-500 hover:text-secondary-700 transition-colors cursor-pointer"
                        >
                          <i className="ri-pencil-line text-sm" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeVariant(v.id)}
                          className="text-red-400 hover:text-red-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {formError && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-md px-3.5 py-2.5">
                  <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 shrink-0" />
                  <p className="text-sm text-red-600">{formError}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 border border-secondary-300 hover:border-secondary-400 text-secondary-700 font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || syncing}
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  {saving || syncing ? (
                    <i className="ri-loader-4-line animate-spin text-base" />
                  ) : (
                    <i className="ri-save-line text-base" />
                  )}
                  {isEditing ? 'Save & sync' : 'Create & sync'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Variant sub-modal */}
      {showVariantForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowVariantForm(false)} />
          <div className="relative bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-heading text-base font-bold text-secondary-900">
                {variantForm.id ? 'Edit variant' : 'New variant'}
              </h4>
              <button
                onClick={() => setShowVariantForm(false)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-secondary-500 hover:bg-secondary-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Variant name</label>
                  <input
                    type="text"
                    value={variantForm.name}
                    onChange={(e) => setVariantForm({ ...variantForm, name: e.target.value })}
                    placeholder="e.g. Deluxe Room"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">SKU</label>
                  <input
                    type="text"
                    value={variantForm.sku}
                    onChange={(e) => setVariantForm({ ...variantForm, sku: e.target.value })}
                    placeholder="e.g. HL-001-DLX"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Option 1 name</label>
                  <input
                    type="text"
                    value={variantForm.option1_name}
                    onChange={(e) => setVariantForm({ ...variantForm, option1_name: e.target.value })}
                    placeholder="e.g. Room type"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Option 1 value</label>
                  <input
                    type="text"
                    value={variantForm.option1_value}
                    onChange={(e) => setVariantForm({ ...variantForm, option1_value: e.target.value })}
                    placeholder="e.g. Deluxe"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Option 2 name</label>
                  <input
                    type="text"
                    value={variantForm.option2_name}
                    onChange={(e) => setVariantForm({ ...variantForm, option2_name: e.target.value })}
                    placeholder="e.g. Vehicle"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Option 2 value</label>
                  <input
                    type="text"
                    value={variantForm.option2_value}
                    onChange={(e) => setVariantForm({ ...variantForm, option2_value: e.target.value })}
                    placeholder="e.g. Limousine"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Option 3 name</label>
                  <input
                    type="text"
                    value={variantForm.option3_name}
                    onChange={(e) => setVariantForm({ ...variantForm, option3_name: e.target.value })}
                    placeholder="e.g. Meal plan"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Option 3 value</label>
                  <input
                    type="text"
                    value={variantForm.option3_value}
                    onChange={(e) => setVariantForm({ ...variantForm, option3_value: e.target.value })}
                    placeholder="e.g. Full board"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Variant price</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={variantForm.price}
                    onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })}
                    placeholder="199"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Compare at price</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={variantForm.compare_at_price}
                    onChange={(e) => setVariantForm({ ...variantForm, compare_at_price: e.target.value })}
                    placeholder="299"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Stock quantity</label>
                  <input
                    type="number"
                    min="0"
                    value={variantForm.stock_quantity}
                    onChange={(e) => setVariantForm({ ...variantForm, stock_quantity: e.target.value })}
                    placeholder="10"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Variant image URL</label>
                  <input
                    type="text"
                    value={variantForm.image}
                    onChange={(e) => setVariantForm({ ...variantForm, image: e.target.value })}
                    placeholder="https://..."
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowVariantForm(false)}
                  className="inline-flex items-center justify-center gap-2 border border-secondary-300 hover:border-secondary-400 text-secondary-700 font-semibold text-sm px-4 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveVariant}
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm px-4 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line text-base" />
                  Save variant
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}